import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { KeyRound, CheckCircle, Mail } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPassword = () => {
  const { t } = useLanguage();
  const [step, setStep] = useState(1); // 1=email, 2=code+password, 3=success
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setStep(2);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to send code. Please try again.');
    }
    finally { setLoading(false); }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password/verify`, { email: email.trim().toLowerCase(), code, new_password: newPassword });
      setStep(3);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to reset password. Please try again.');
    }
    finally { setLoading(false); }
  };

  if (step === 3) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="w-full max-w-md text-center">
          <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#1F2937] mb-2">Password Reset!</h1>
          <p className="text-[#6B7280] mb-6">You can now login with your new password.</p>
          <Link to="/login" className="inline-block bg-[#0F4C5C] text-white rounded-full px-8 py-3 font-semibold hover:bg-[#0F4C5C]/90 transition-all" data-testid="back-to-login">
            {t.signIn}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          {step === 1 ? <KeyRound className="w-12 h-12 text-[#0F4C5C] mx-auto mb-4" /> : <Mail className="w-12 h-12 text-[#0F4C5C] mx-auto mb-4" />}
          <h1 className="text-2xl font-bold text-[#1F2937] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            {step === 1 ? 'Reset Password' : 'Enter Reset Code'}
          </h1>
          <p className="text-[#6B7280]">
            {step === 1 ? 'Enter your email to receive a reset code' : `We sent a 6-digit code to ${email}`}
          </p>
        </div>

        {step === 1 && (
          <form onSubmit={handleSendCode} className="space-y-5" data-testid="forgot-password-form">
            <div>
              <label className="block text-sm font-medium text-[#1F2937] mb-2">{t.email}</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder={t.emailPlaceholder}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] text-[16px]"
                data-testid="forgot-email" />
            </div>
            {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="forgot-error">{error}</div>}
            <button type="submit" disabled={loading}
              className="w-full bg-[#0F4C5C] text-white hover:bg-[#0F4C5C]/90 rounded-full px-6 py-3 font-semibold transition-all shadow-lg disabled:opacity-50 text-[16px]"
              data-testid="forgot-submit">
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleVerifyCode} className="space-y-5" data-testid="verify-code-form">
            <div>
              <label className="block text-sm font-medium text-[#1F2937] mb-2">6-Digit Code</label>
              <input type="text" value={code} onChange={e => setCode(e.target.value)} required maxLength={6} pattern="[0-9]{6}" inputMode="numeric"
                placeholder="000000"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] text-[16px] text-center text-2xl tracking-[0.5em] font-mono"
                data-testid="reset-code" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1F2937] mb-2">New Password</label>
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={6} placeholder="Min 6 characters"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] text-[16px]"
                data-testid="reset-new-password" />
            </div>
            {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="forgot-error">{error}</div>}
            <button type="submit" disabled={loading}
              className="w-full bg-[#0F4C5C] text-white hover:bg-[#0F4C5C]/90 rounded-full px-6 py-3 font-semibold transition-all shadow-lg disabled:opacity-50 text-[16px]"
              data-testid="verify-submit">
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
            <button type="button" onClick={() => { setStep(1); setError(''); }}
              className="w-full text-[#6B7280] hover:text-[#1F2937] text-sm font-medium py-2 transition-colors">
              Didn't receive the code? Go back
            </button>
          </form>
        )}

        <div className="mt-6 text-center">
          <Link to="/login" className="text-[#0F4C5C] hover:text-[#0F4C5C]/80 font-semibold" data-testid="back-link">
            &larr; {t.signIn}
          </Link>
          <p className="mt-4 text-sm text-[#6B7280]">Need help? Contact us at <a href="mailto:info@younesdental.com" className="text-[#0F4C5C] font-medium">info@younesdental.com</a></p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
